import {
  AtoneAuthenticateGetResponseMemberTypeEnum,
  AtoneAuthenticateGetResponseSexDivisionEnum,
  AtoneMemberTypePostResponseMemberTypeEnum,
  AtoneRequestTokenPostRequestSexDivisionEnum,
  AtoneTracksPostRequestAuthenticationTypeEnum,
  LiteCapturePostResponseAuthorizationResultEnum,
} from '@netprotections/berlin-bff';
import { reactive, readonly, ref, unref, computed, watch, Ref } from '@vue/composition-api';
import { useQuery, useMutation } from 'vue-query';
import { Route } from 'vue-router';

import { useContext } from '@berlin-front/common/composables/context';
import { useRouter } from '@berlin-front/common/composables/useRouter';
import useFingerprint from '@berlin-front/common/composables/useFingerprint';
import useOrAll from '@berlin-front/common/composables/useOrAll';
import { useSessionId } from '@berlin-front/common/composables/useSessionId';
import { usePrepaymentCheck } from '@services/service-type-bridge/atone-common';
import { event, set } from 'vue-gtag';
import { SERVICE_TYPE } from '@/services/ModalParameterContext';
import { broadcast } from '@/broadcast';
import { BerlinBffContext } from '@/services/BerlinBff';
import { ApiCacheContext } from '@/services/ApiCacheContext';
import { getRefDeep, notNil, pick, ValueTypeOf } from '@berlin-front/common/utils';
import { ROUTE_NAME } from '@/router/route-name';
import useIsLegacy from '@composables/useIsLegacy';
import { ModalBridgeContext } from '@services/ModalBridgeContext';

const useRequestTokenPost = () => {
  const { atoneLiteModalParameter, setAtoneRequestTokenPostResult } = useContext(ApiCacheContext);
  const { requestToken, payment } = useContext(BerlinBffContext);
  const globals = useContext(ModalBridgeContext);

  // リクエストトークン発行API
  const {
    //
    isLoading,
    isSuccess,
    isError,
    data,
  } = useQuery(
    // 条件変更時のリクエスト実行は行わないため、クエリキーは定数で良い。
    'atoneLite.RequestTokenPost',
    // stateにパラメタセットして、そっから始めるのがuseQueryの作法。
    () =>
      Promise.all([
        requestToken.atoneRequestTokenControllerRequestTokenPost({
          shopPublicKey: atoneLiteModalParameter.value.pubKey, // 必須パラメータ。
          mailAddress: atoneLiteModalParameter.value.payment.customer?.email,
          familyName: atoneLiteModalParameter.value.payment.customer?.customerFamilyName,
          familyNameKana: atoneLiteModalParameter.value.payment.customer?.customerFamilyNameKana,
          givenName: atoneLiteModalParameter.value.payment.customer?.customerGivenName,
          givenNameKana: atoneLiteModalParameter.value.payment.customer?.customerGivenNameKana,
          // TODO: Enum調整してasとる
          sexDivision: atoneLiteModalParameter.value.payment.customer
            ?.sexDivision as unknown as AtoneRequestTokenPostRequestSexDivisionEnum,
          birthday: atoneLiteModalParameter.value.payment.customer?.birthday,
          zipCode: atoneLiteModalParameter.value.payment.customer?.zipCode,
          address: atoneLiteModalParameter.value.payment.customer?.address,
          phoneNumber: atoneLiteModalParameter.value.payment.customer?.phoneNumber,
        }),
        payment.atonePaymentControllerParameterPost(
          atoneLiteModalParameter.value.pubKey,
          {
            requestId: globals.requestId,
            serviceType: '04',
            modalMode: '01',
            ...pick(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              atoneLiteModalParameter.value.payment as unknown as any,
              'amount',
              'checksum',
              'customer',
              'description',
              'destCustomers',
              'items',
              'salesForm',
              'salesSettled',
              'serviceSupplier',
              'shopTransactionNo',
              'sourceMedia',
              'transactionOptions',
              'userNo',
            ),
          },
          atoneLiteModalParameter.value.terminalId,
        ),
      ] as const).then((v) => v[0]),
    {
      onSuccess: ({ data: result }) => {
        setAtoneRequestTokenPostResult(result);
      },
    },
  );
  return { isLoading, isSuccess, isError, data };
};

const useLoginWithPretoken = () => {
  const { authenticate } = useContext(BerlinBffContext);
  const { atoneLiteModalParameter, tokens, setAtoneAuthenticateGetResult } = useContext(ApiCacheContext);

  const { isLoading, isSuccess, isError, data } = useQuery(
    'atoneLite.authenticateGet',
    () =>
      authenticate.atoneAuthenticateControllerAuthenticateGet(
        tokens.value?.npToken ?? '',
        atoneLiteModalParameter.value.pubKey,
        useSessionId(),
      ),
    {
      enabled: computed(() => (tokens.value?.npToken ?? '').length > 0),
      onSuccess: ({ data: result }) => {
        setAtoneAuthenticateGetResult(result);
        window.newrelic.addPageAction('authenticate_by_token', {
          member_id: notNil(result.memberCode),
          shop_id: notNil(result.shopId),
          shop_name: notNil(result.shopName),
          gaid: notNil(result.hashId),
        });

        let gender: string | null;
        if (result.memberType === AtoneAuthenticateGetResponseMemberTypeEnum.NUMBER_3) {
          gender = null;
          // eslint-disable-next-line no-underscore-dangle
        } else if (result.sexDivision === AtoneAuthenticateGetResponseSexDivisionEnum._1) {
          gender = '男性';
          // eslint-disable-next-line no-underscore-dangle
        } else if (result.sexDivision === AtoneAuthenticateGetResponseSexDivisionEnum._2) {
          gender = '女性';
        } else {
          gender = '不明';
        }

        set({
          user_id: notNil(result.memberCode),
          user_properties: {
            member_id: notNil(result.memberCode),
            shop_id: notNil(result.shopId),
            gender,
            age: result.memberType === AtoneAuthenticateGetResponseMemberTypeEnum.NUMBER_3 ? null : result.age,
            // shop_name: notNil(result.shopName),
            guest: result.memberType === AtoneAuthenticateGetResponseMemberTypeEnum.NUMBER_3 ? 'yes' : 'no',
          },
        });

        event('login', {
          method: 'Token Lite',
        });
      },
    },
  );
  return {
    isLoading: computed(() => ((tokens.value?.npToken ?? '').length > 0 ? isLoading.value : false)),
    isSuccess: computed(() => ((tokens.value?.npToken ?? '').length > 0 ? isSuccess.value : false)),
    isError: computed(() => ((tokens.value?.npToken ?? '').length > 0 ? isError.value : false)),
    data: computed(() => ((tokens.value?.npToken ?? '').length > 0 ? data.value : undefined)),
  };
};

/**
 * fingerprint check
 * チェックがダメだった場合は、sms送信までをここで行う。
 * @param param
 * @returns
 */
const useFingerprintCheck = (param: { enabled: Ref<boolean> }) => {
  const fingerprint = useFingerprint();
  const {
    tokens,
    atoneLiteModalParameter,
    setAtoneTracksPostResult,
    setAtoneTracksTrustResult,
    setAtoneSmsNotifyPostResult,
  } = useContext(ApiCacheContext);
  const { tracks, sms } = useContext(BerlinBffContext);

  const {
    isLoading: isTrackTokenPostLoading,
    isSuccess: isTrackTokenPostSucceeded,
    isError: isTrackTokenPostError,
    data: trackTokenPostResult,
  } = useQuery(
    'atoneLite.init.trackTokenPost',
    () =>
      tracks.atoneTracksControllerTrackTokenPost(
        notNil(tokens.value.npToken),
        notNil(atoneLiteModalParameter.value.pubKey),
        {
          authenticationType: AtoneTracksPostRequestAuthenticationTypeEnum.NUMBER_1,
          fingerPrint: fingerprint.value,
        },
      ),
    {
      enabled: computed(() => fingerprint.resolved && param.enabled.value),
      onSuccess({ data }) {
        setAtoneTracksPostResult(data);
      },
    },
  );

  const {
    isLoading: isTrackTokenTrustLoading,
    isSuccess: isTrackTokenTrustSucceeded,
    isError: isTrackTokenTrustError,
    data: trackTokenTrustResult,
  } = useQuery(
    'atoneLite.init.TrackTokenTrust',
    () =>
      tracks.atoneTracksControllerTrackTokenTrust(
        notNil(trackTokenPostResult.value?.data.trackToken),
        notNil(tokens.value.npToken),
        notNil(atoneLiteModalParameter.value.pubKey),
      ),
    {
      enabled: isTrackTokenPostSucceeded,
      onSuccess({ data }) {
        setAtoneTracksTrustResult(data);
      },
    },
  );
  const isSuccess = computed(
    () => isTrackTokenTrustSucceeded.value && (trackTokenTrustResult.value?.data.trustedFlag ?? false),
  );

  // trackTokenがtrustされてない時はそのまま次のsms認証のページのためにsmsNotifyを事前呼び出しする。
  const {
    isLoading: isSmsNotifyPostLoading,
    isSuccess: isSmsNotifyPostSucceeded,
    isError: isSmsNotifyPostError,
  } = useQuery(
    'atoneLite.init.SmsNotifyPost',
    () =>
      sms.atoneSmsControllerSmsNotifyPost(notNil(tokens.value.npToken), notNil(atoneLiteModalParameter.value.pubKey), {
        trackToken: notNil(trackTokenPostResult.value?.data.trackToken),
        resend: false,
      }),
    {
      // trustedFlagはundefinedのケースもある。明示的にfalseであることが必要なため、等号比較する必要がある。
      enabled: computed(() => trackTokenTrustResult.value?.data.trustedFlag === false),
      onSuccess({ data }) {
        setAtoneSmsNotifyPostResult(data);
      },
    },
  );
  // トラックトークンが認証済みでない場合、smsNotifyを送信するが、その結果が成否かかわらず実行が完了した段階でfailとする。
  // このfailを検出すると、sms認証画面へと遷移する。
  const isFail = useOrAll(isSmsNotifyPostSucceeded, isSmsNotifyPostError);

  return {
    isLoading: useOrAll(isTrackTokenPostLoading, isTrackTokenTrustLoading, isSmsNotifyPostLoading),
    isSuccess,
    isFail,
    isError: useOrAll(isTrackTokenTrustError, isTrackTokenPostError),
  };
};

/**
 * 決済。
 * @param param
 * @returns
 */
const useSettlement = (param: { enabled: Ref<boolean> }) => {
  const [isLegacy] = useIsLegacy();
  const { tokens, atoneLiteModalParameter, setLiteCapturePostResult, liteCapturePostResult } =
    useContext(ApiCacheContext);
  const { napoleon, js } = useContext(BerlinBffContext);
  const globals = useContext(ModalBridgeContext);
  const {
    isSuccess: isSuccessJsPayment,
    isError: isErrorJsPayment,
    mutate: mutateJsPaymentCapture,
  } = useMutation(
    'lite.JsPaymentCapturePost',
    () =>
      js.atoneJavascriptControllerJavascriptPaymentCapture({
        id: getRefDeep(liteCapturePostResult, 'id'),
      }),
    {},
  );

  const {
    mutate,
    isLoading,
    isError,
    isSuccess: isSuccessPayment,
    data: axiosResponseRef,
  } = useMutation(
    () =>
      napoleon.liteControllerCapturePost(
        notNil(tokens.value.npToken),
        notNil(atoneLiteModalParameter.value.pubKey),
        notNil(tokens.value.trustedTrackToken),
        {
          checksum: atoneLiteModalParameter.value.payment.checksum,
          shopTransactionNo: atoneLiteModalParameter.value.payment.shopTransactionNo,
          amount: atoneLiteModalParameter.value.payment.amount,
          items: atoneLiteModalParameter.value.payment.items,
          // TODO: any変換をなんとかブチとる。これenumの型がrequest型ごとに別で設定されてるせいなんだけど。
          customer: (atoneLiteModalParameter.value.payment.customer as unknown as any) ?? undefined, // eslint-disable-line @typescript-eslint/no-explicit-any
          description: atoneLiteModalParameter.value.payment.description,
          destCustomers: atoneLiteModalParameter.value.payment.destCustomers,
          salesForm: atoneLiteModalParameter.value.payment.salesForm as unknown as any, // eslint-disable-line @typescript-eslint/no-explicit-any
          salesSettled: atoneLiteModalParameter.value.payment.salesSettled,
          serviceSupplier: atoneLiteModalParameter.value.payment.serviceSupplier as unknown as any, // eslint-disable-line @typescript-eslint/no-explicit-any
          sourceMedia: atoneLiteModalParameter.value.payment.sourceMedia,
          // eslint-disable-line @typescript-eslint/no-explicit-any
          transactionOptions: atoneLiteModalParameter.value.payment.transactionOptions as unknown as any, // eslint-disable-line @typescript-eslint/no-explicit-any
          userNo: atoneLiteModalParameter.value.payment.userNo,
          isOldParameter: isLegacy.value,
          requestId: globals.requestId,
        },
      ),
    {
      onSuccess({ data }) {
        setLiteCapturePostResult(data);
        if (data.authorizationResult === LiteCapturePostResponseAuthorizationResultEnum.NUMBER_1)
          mutateJsPaymentCapture();
      },
    },
  );
  const data = computed(() => axiosResponseRef.value?.data);
  const isSuccess = computed(() => {
    if (liteCapturePostResult.value) {
      if (liteCapturePostResult.value.authorizationResult === LiteCapturePostResponseAuthorizationResultEnum.NUMBER_1) {
        return unref(isSuccessPayment) && unref(useOrAll(isSuccessJsPayment, isErrorJsPayment));
      }
      return unref(isSuccessPayment);
    }
    return false;
  });

  watch([param.enabled] as const, (enable) => {
    if (enable) {
      mutate();
    }
  });

  return {
    isLoading,
    isSuccess,
    isError,
    data,
  };
};

const useMemberTypePost = (param: { enabled: Ref<boolean> }) => {
  const { member } = useContext(BerlinBffContext);
  const { tokens, atoneLiteModalParameter, setAtoneMemberTypePostResult } = useContext(ApiCacheContext);
  const { isLoading, isSuccess, isError, data } = useQuery(
    'atoneLite.MemberTypePost',
    async () => {
      if (atoneLiteModalParameter.value.payment.customer?.email) {
        return member.atoneMemberControllerMemberTypePost(notNil(tokens.value.requestToken), {
          mailAddress: atoneLiteModalParameter.value.payment.customer?.email ?? '',
        });
      }
      // payment.customer.emailが設定されてない時は無条件で新規会員になるため、APIを呼ばない。
      // API呼んだ体で新規会員レスポンスを返す。
      return Promise.resolve({
        data: {
          memberType: AtoneMemberTypePostResponseMemberTypeEnum.NUMBER_0,
        },
      });
    },
    {
      enabled: computed(
        () =>
          param.enabled.value &&
          // (tokens.value?.npToken ?? '').length > 0 &&
          (tokens.value?.requestToken ?? '').length > 0,
      ),

      onSuccess: ({ data: result }) => {
        setAtoneMemberTypePostResult(result);
      },
    },
  );
  return {
    isLoading,
    isSuccess,
    isError,
    data,
  };
};

const usePrepaymentCheckPost = (param: { enabled: Ref<boolean> }) => {
  const router = useRouter();
  const prePaymentCheck = usePrepaymentCheck();
  const {
    mutate,
    isSuccess,
    data: prePaymentCheckResult,
  } = useMutation('lite.prePaymentCheck', async () => prePaymentCheck());

  watch([param.enabled] as const, (enabled) => {
    if (enabled) mutate();
  });

  const checkIsOk = ref(false);
  watch([isSuccess, prePaymentCheckResult] as const, ([success, result]) => {
    if (success) {
      switch (result) {
        case true: {
          checkIsOk.value = true;
          break;
        }
        case false: {
          // TODO: liteのカート不整合画面ルート作成したい。
          // カート不整合画面へ遷移。
          void router.replace({ name: ROUTE_NAME.ATONE_FAIL_CART });
          break;
        }
        default: {
          // 網羅性検証
          ((_v: undefined) => {})(result);
        }
      }
    }
  });
  return {
    isSuccess: checkIsOk,
  };
};

export const useLoading = (_$route: Route) => ({
  useLoading: () => {
    const router = useRouter();
    const { atoneAuthenticateGetResult, atoneMemberTypePostResult, liteCapturePostResult, atoneLiteModalParameter } =
      useContext(ApiCacheContext);
    const nextPage = ref<
      undefined | { name: ValueTypeOf<typeof ROUTE_NAME>; query?: Record<string, string | string[]> }
    >();

    // リクエストトークンを得る。
    useRequestTokenPost();
    // - preTokenでログイン
    const { isSuccess: isLoginSucceeded, isError: isLoginFailed } = useLoginWithPretoken();
    //   - フィンガープリントチェック。
    const { isSuccess: isFingerprintCheckSucceeded, isFail: isFingerprintCheckFailed } = useFingerprintCheck({
      enabled: isLoginSucceeded,
    });
    //     - フィンガープリントOK -> プリペイメントチェック
    const { isSuccess: isSuccessPrepaymentCheck } = usePrepaymentCheckPost({ enabled: isFingerprintCheckSucceeded });
    //     - プリペイメントチェックOK -> 決済処理
    const { isSuccess: isSettlementSucceeded } = useSettlement({
      enabled: isSuccessPrepaymentCheck,
    });
    const captureSucceeded = ref(false);
    watch([isSettlementSucceeded, liteCapturePostResult], () => {
      if (isSettlementSucceeded.value && liteCapturePostResult.value) {
        switch (liteCapturePostResult.value.authorizationResult) {
          case LiteCapturePostResponseAuthorizationResultEnum.NUMBER_1: {
            // 審査OK
            captureSucceeded.value = true;
            window.newrelic.addPageAction('payment_complete', {
              revenue: atoneLiteModalParameter.value.payment.amount,
              coupon: 0, // Liteはポイント利用がないので0で固定
              products: atoneLiteModalParameter.value.payment.items.map((item) => ({
                id: item.shopItemId,
                price: item.itemPrice,
                name: item.itemName,
                quantity: item.itemCount,
              })),
              shop_transaction_no: atoneLiteModalParameter.value.payment.shopTransactionNo,
            });

            event('purchase', {
              transaction_id: atoneLiteModalParameter.value.payment.shopTransactionNo,
              value: atoneLiteModalParameter.value.payment.amount,
              currency: 'JPY',
              coupon: 0,
              items: atoneLiteModalParameter.value.payment.items.map((item) => ({
                id: item.shopItemId,
                price: item.itemPrice,
                name: item.itemName,
                quantity: item.itemCount,
              })),
            });

            break;
          }
          case LiteCapturePostResponseAuthorizationResultEnum.NUMBER_3: {
            // 審査中
            // TODO: 既存の着信認証が使えるかどうか確認中。分かり次第実装。
            nextPage.value = { name: ROUTE_NAME.ATONE_AUTH_CALL };
            break;
          }
          default: {
            if (liteCapturePostResult.value.additionalAuthorizationResultNgReason === 1) {
              // 公的証明書確認画面を表示
              nextPage.value = { name: ROUTE_NAME.ATONE_LITE_FAIL_OFFICIAL_IDENTIFICATION };
            } else if (liteCapturePostResult.value.additionalAuthorizationResultNgReason === 2) {
              // アプリ認証画面を表示
              nextPage.value = { name: ROUTE_NAME.ATONE_LITE_FAIL_SPAPP_LOGIN };
            } else {
              // 与信失敗画面を表示
              nextPage.value = { name: ROUTE_NAME.ATONE_LITE_FAIL_AUTH_NG };
            }
            window.newrelic.addPageAction('payment_ng', {
              revenue: atoneLiteModalParameter.value.payment.amount,
              coupon: 0, // Liteはポイント利用がないので0で固定
              products: atoneLiteModalParameter.value.payment.items.map((item) => ({
                id: item.shopItemId,
                price: item.itemPrice,
                name: item.itemName,
                quantity: item.itemCount,
              })),
              shop_transaction_no: atoneLiteModalParameter.value.payment.shopTransactionNo,
            });
          }
        }
      }
    });
    //     - フィンガープリントNG -> SMS認証
    watch([isLoginSucceeded, isFingerprintCheckFailed], () => {
      if (isFingerprintCheckFailed.value && isLoginSucceeded.value && atoneAuthenticateGetResult.value) {
        // SMSからの戻り先はAtoneログイン。
        nextPage.value = {
          name: ROUTE_NAME.ATONE_LITE_AUTH_SMS_ATONE,
          query: {
            previous: ROUTE_NAME.ATONE_LITE_LOGIN_ATONE,
          },
        };
      }
    });

    // - ログインNG || preTokenなし
    const { tokens } = useContext(ApiCacheContext);
    const preTokenEmpty = computed(() => (tokens.value.npToken ?? '').length === 0);
    const memberTypePostExec = computed(() => isLoginFailed.value || preTokenEmpty.value);
    //   - メンバー種別判定
    useMemberTypePost({ enabled: memberTypePostExec });
    //     - 会員 -> ログイン画面
    //     - NP会員 -> NP会員ログイン画面
    //     - その他 -> 仮会員ログイン画面
    watch([atoneMemberTypePostResult], () => {
      if (atoneMemberTypePostResult.value && 'memberType' in atoneMemberTypePostResult.value) {
        switch (notNil(atoneMemberTypePostResult.value.memberType)) {
          // NP会員。
          case AtoneMemberTypePostResponseMemberTypeEnum.NUMBER_1: {
            nextPage.value = {
              name: ROUTE_NAME.ATONE_LITE_MEMBER_NP_LOGIN,
            };
            break;
          }
          // 会員
          case AtoneMemberTypePostResponseMemberTypeEnum.NUMBER_2: {
            nextPage.value = {
              name: ROUTE_NAME.ATONE_LITE_LOGIN_ATONE,
            };
            break;
          }
          // 新規
          // 仮会員
          case AtoneMemberTypePostResponseMemberTypeEnum.NUMBER_0:
          case AtoneMemberTypePostResponseMemberTypeEnum.NUMBER_3: {
            nextPage.value = {
              name: ROUTE_NAME.ATONE_LITE_LOGIN_GUEST,
            };
            break;
          }
          default: {
            // 例外。
          }
        }
      }
    });

    // 進捗バー用。
    // TODO: 決済完了/NGもcomplete条件に入れる。
    const isCompleted = computed(() => nextPage.value !== undefined || captureSucceeded.value);
    const isLoading = computed(() => !unref(isCompleted));

    // ローディング終了時の処理
    const onComplete = () => {
      const location = unref(nextPage);
      if (captureSucceeded.value) {
        broadcast.send({ type: 'MODAL_CONTROL_SUCCEEDED', payload: { serviceType: SERVICE_TYPE.ATONE_LITE } });
        return;
      }
      if (location) {
        void router.replace(location);
      }
      // TODO: 決済完了でモーダル閉じる処理、NGでエラー画面に遷移する処理も入れること。
    };

    return readonly(
      reactive({
        title: '',
        duration: 5000,
        onComplete,
        isLoading,
        isCompleted,
      }),
    );
  },
});
